import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { RefreshCw, CalendarClock, Trash2, LogOut } from "lucide-react";
import { AdminLogin } from "../components/AdminLogin";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmt = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

export default function AdminPage() {
  const [authChecked, setAuthChecked] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scheduleDrafts, setScheduleDrafts] = useState({});

  const checkAuth = async () => {
    try {
      await axios.get(`${API}/admin/me`, { withCredentials: true });
      setAuthenticated(true);
    } catch {
      setAuthenticated(false);
    } finally {
      setAuthChecked(true);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [bRes, lRes] = await Promise.all([
        axios.get(`${API}/bookings`, { withCredentials: true }),
        axios.get(`${API}/leads`, { withCredentials: true }),
      ]);
      setBookings(bRes.data);
      setLeads(lRes.data);
    } catch {
      toast.error("Could not load admin data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (authenticated) load();
  }, [authenticated]);

  useEffect(() => {
    if (!authenticated) return;
    const interval = setInterval(load, 20000);
    return () => clearInterval(interval);
  }, [authenticated]);

  const handleLogout = async () => {
    await axios.post(`${API}/admin/logout`, {}, { withCredentials: true });
    setAuthenticated(false);
  };

  const saveSchedule = async (id) => {
    const localValue = scheduleDrafts[id];
    if (!localValue) {
      toast.error("Pick a date and time first.");
      return;
    }
    try {
      const isoValue = new Date(localValue).toISOString();
      await axios.patch(`${API}/bookings/${id}/schedule`, { scheduled_at: isoValue }, { withCredentials: true });
      toast.success("Call time saved. Reminder will send 24h before.");
      load();
    } catch {
      toast.error("Could not save the call time.");
    }
  };

  const deleteBooking = async (id) => {
    try {
      await axios.delete(`${API}/bookings/${id}`, { withCredentials: true });
      toast.success("Booking removed.");
      setBookings((b) => b.filter((x) => x.id !== id));
    } catch {
      toast.error("Could not remove this booking.");
    }
  };

  const deleteLead = async (id) => {
    try {
      await axios.delete(`${API}/leads/${id}`, { withCredentials: true });
      toast.success("Lead removed.");
      setLeads((l) => l.filter((x) => x.id !== id));
    } catch {
      toast.error("Could not remove this lead.");
    }
  };

  if (!authChecked) {
    return <main className="min-h-screen bg-[#FBF9F5]" data-testid="admin-page-loading" />;
  }

  if (!authenticated) {
    return <AdminLogin onSuccess={() => setAuthenticated(true)} />;
  }

  return (
    <main data-testid="admin-page" className="min-h-screen px-6 py-24 bg-[#FBF9F5]">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <h1 className="font-serif text-3xl text-[#1E2D24]">Aurea Soul — Admin</h1>
          <div className="flex items-center gap-2">
            <button
              data-testid="admin-refresh-btn"
              onClick={load}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#4A6B56]/25 text-[#4A6B56] text-sm hover:bg-[#E2EAE4] transition-colors"
            >
              <RefreshCw className="w-4 h-4" /> Refresh
            </button>
            <button
              data-testid="admin-logout-btn"
              onClick={handleLogout}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#4A6B56]/25 text-[#4A6B56] text-sm hover:bg-[#E2EAE4] transition-colors"
            >
              <LogOut className="w-4 h-4" /> Log out
            </button>
          </div>
        </div>

        <section className="mb-14">
          <h2 className="font-serif text-2xl text-[#1E2D24] mb-4">Bookings ({bookings.length})</h2>
          <div className="overflow-x-auto rounded-2xl border border-[#4A6B56]/15" data-testid="bookings-table">
            <table className="w-full text-sm">
              <thead className="bg-[#E2EAE4] text-[#1E2D24] text-left">
                <tr>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Preferred</th>
                  <th className="px-4 py-3">Segment</th>
                  <th className="px-4 py-3">Scheduled Call</th>
                  <th className="px-4 py-3">Reminder</th>
                  <th className="px-4 py-3">Set Call Time</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {!loading && bookings.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-4 py-6 text-center text-[#5F6A62]">
                      No bookings yet.
                    </td>
                  </tr>
                )}
                {bookings.map((b) => (
                  <tr key={b.id} className="border-t border-[#4A6B56]/10" data-testid={`booking-row-${b.id}`}>
                    <td className="px-4 py-3">{b.name}</td>
                    <td className="px-4 py-3">{b.email}</td>
                    <td className="px-4 py-3">{b.preferred_time} ({b.timezone_pref})</td>
                    <td className="px-4 py-3 capitalize">{b.segment}</td>
                    <td className="px-4 py-3">{fmt(b.scheduled_at)}</td>
                    <td className="px-4 py-3">{b.reminder_sent ? "Sent" : "Pending"}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <input
                          data-testid={`schedule-input-${b.id}`}
                          type="datetime-local"
                          onChange={(e) => setScheduleDrafts((d) => ({ ...d, [b.id]: e.target.value }))}
                          className="px-2 py-1.5 rounded-lg border border-[#4A6B56]/20 text-xs"
                        />
                        <button
                          data-testid={`schedule-save-btn-${b.id}`}
                          onClick={() => saveSchedule(b.id)}
                          className="p-1.5 rounded-lg bg-[#4A6B56] text-white hover:opacity-90"
                          title="Save call time"
                        >
                          <CalendarClock className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        data-testid={`delete-booking-btn-${b.id}`}
                        onClick={() => deleteBooking(b.id)}
                        className="p-1.5 rounded-lg border border-red-300 text-red-500 hover:bg-red-50"
                        title="Delete booking"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section>
          <h2 className="font-serif text-2xl text-[#1E2D24] mb-4">Leads ({leads.length})</h2>
          <div className="overflow-x-auto rounded-2xl border border-[#4A6B56]/15" data-testid="leads-table">
            <table className="w-full text-sm">
              <thead className="bg-[#E2EAE4] text-[#1E2D24] text-left">
                <tr>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Segment</th>
                  <th className="px-4 py-3">Booked</th>
                  <th className="px-4 py-3">Nurture Stage</th>
                  <th className="px-4 py-3">Last Nurture</th>
                  <th className="px-4 py-3">Captured</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {!loading && leads.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-6 text-center text-[#5F6A62]">
                      No leads captured yet.
                    </td>
                  </tr>
                )}
                {leads.map((l) => (
                  <tr key={l.id} className="border-t border-[#4A6B56]/10" data-testid={`lead-row-${l.id}`}>
                    <td className="px-4 py-3">{l.email}</td>
                    <td className="px-4 py-3 capitalize">{l.segment}</td>
                    <td className="px-4 py-3">{l.booked ? "Yes" : "No"}</td>
                    <td className="px-4 py-3">{l.nurture_sent_count} / 3</td>
                    <td className="px-4 py-3">{fmt(l.last_nurture_at)}</td>
                    <td className="px-4 py-3">{fmt(l.created_at)}</td>
                    <td className="px-4 py-3">
                      <button
                        data-testid={`delete-lead-btn-${l.id}`}
                        onClick={() => deleteLead(l.id)}
                        className="p-1.5 rounded-lg border border-red-300 text-red-500 hover:bg-red-50"
                        title="Delete lead"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </main>
  );
}
